import React, { useState } from 'react';
import {
  Box, Modal, TextField, Stack, Alert, Snackbar, CircularProgress,
  IconButton, Divider, Typography, Button, useTheme, useMediaQuery
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';

interface CreateTicketModalProps {
  open: boolean;
  onClose: () => void;
  defaultEmail?: string;
}

const CreateTicketModal: React.FC<CreateTicketModalProps> = ({ open, onClose, defaultEmail }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [formData, setFormData] = useState({ topic: '', description: '', email: defaultEmail || '' });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleClose = () => {
    if (loading) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!formData.topic || !formData.description || !formData.email) {
      setSnackbar({ open: true, message: 'Заполните все поля', severity: 'error' });
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSnackbar({ open: true, message: 'Введите корректный email', severity: 'error' });
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post('/api/help/send-request', formData);
      if (response.data.success) {
        setSnackbar({ open: true, message: 'Заявка успешно отправлена!', severity: 'success' });
        setFormData({ topic: '', description: '', email: defaultEmail || '' });
        setTimeout(() => onClose(), 800);
      } else {
        setSnackbar({ open: true, message: response.data.error || 'Ошибка отправки', severity: 'error' });
      }
    } catch (error: any) {
      console.error('Ошибка:', error);
      const msg = error.response?.data?.error || error.response?.data?.details || 'Ошибка отправки. Попробуйте позже.';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Modal open={open} onClose={handleClose}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '92%', sm: 500 },
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: { xs: 2, sm: 4 },
          borderRadius: 2,
          maxHeight: '90vh',
          overflow: 'auto',
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" fontWeight="bold">Создание заявки</Typography>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Опишите проблему или вопрос, и мы свяжемся с вами в ближайшее время.
          </Typography>
          <Stack spacing={2}>
            <TextField
              label="Тема"
              name="topic"
              value={formData.topic}
              onChange={handleChange}
              fullWidth
              required
              placeholder="Кратко опишите суть"
              size={isMobile ? 'small' : 'medium'}
            />
            <TextField
              label="Описание"
              name="description"
              value={formData.description}
              onChange={handleChange}
              fullWidth
              required
              multiline
              rows={4}
              placeholder="Подробно опишите проблему или вопрос"
              size={isMobile ? 'small' : 'medium'}
            />
            <TextField
              label="Ваш Email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              fullWidth
              required
              type="email"
              placeholder="Для обратной связи"
              size={isMobile ? 'small' : 'medium'}
            />
            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
              <Button variant="outlined" onClick={handleClose} fullWidth size="large" disabled={loading}>
                Отмена
              </Button>
              <Button variant="contained" onClick={handleSubmit} disabled={loading} fullWidth size="large">
                {loading ? <CircularProgress size={24} /> : 'Отправить'}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        sx={{ bottom: 24, left: 24, maxWidth: 400 }}
      >
        <Alert severity={snackbar.severity} sx={{ borderRadius: 1, width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CreateTicketModal;
